export const SLURRY_D182_PH_NAME = 'D182 (PH)';

export const POTASSIUM_PERCENT_MATERIAL_ID = 898;
export const POTASSIUM_WEIGHT_MATERIAL_ID = 899;
export const SODIUM_PERCENT_MATERIAL_ID = 900;
export const SODIUM_WEIGHT_MATERIAL_ID = 901;

const WATER_DENSITY = 8.34;
const POTASSIUM_DRY_SPECIFIC_GRAVITY = 1.99;
const SODIUM_DRY_SPECIFIC_GRAVITY = 2.15;

const POTASSIUM_SPECIFIC_GRAVITY_TABLE: ReadonlyArray<readonly [number, number]> = [
  [0, 2.75],
  [1, 2.753394936],
  [2, 2.728364073],
  [3, 2.703784216],
  [4, 2.66773376],
  [10, 2.592829789],
  [15, 2.52732672],
  [20, 2.495800815],
  [25, 2.460000394],
  [30, 2.4350511],
  [31.58, 2.425212509]
];

const SODIUM_SPECIFIC_GRAVITY_TABLE: ReadonlyArray<readonly [number, number]> = [
  [0, 3.35],
  [5, 3.262174435],
  [6, 3.227097291],
  [10, 3.118130369],
  [12, 3.070281821],
  [15, 3.01628189],
  [18, 2.964148622],
  [20, 2.935159394],
  [24, 2.878849381],
  [25, 2.871962182],
  [30, 2.81802862],
  [35.14, 2.7724715756]
];

interface NaturalSplinePoint {
  x: number;
  a: number;
  b: number;
  c: number;
  d: number;
}

export function toFiniteNumber(value: unknown): number {
  // Number() requires the whole string to be numeric, matching decimal.TryParse
  // rather than parseFloat's legacy-incompatible prefix acceptance ("12abc" → 12).
  const text = `${value ?? ''}`.trim();
  const parsed = typeof value === 'number' ? value : (text === '' ? 0 : Number(text));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sumFiniteNumbers(values: Iterable<unknown>): number {
  let total = 0;
  for (const value of values) {
    total += toFiniteNumber(value);
  }
  return total;
}

export function formatFixedNumber(value: unknown, decimals: number): string {
  return toFiniteNumber(value).toFixed(decimals);
}

export function minutesToHHMM(minutes: number | null | undefined): string {
  if (minutes == null) {
    return '';
  }

  // C# integer division truncates toward zero (unlike Math.floor).
  const h = Math.trunc(minutes / 60);
  const m = minutes - (h * 60);
  if (h < 0 || m < 0) {
    return `-${Math.abs(h)}:${Math.abs(m).toString().padStart(2, '0')}`;
  }
  return `${h}:${m.toString().padStart(2, '0')}`;
}

export function parseLegacyTimeToMinutes(value: string): number | undefined {
  if (!value || !value.trim()) {
    return undefined;
  }

  const text = value.trim();
  const delimiterIndex = text.indexOf(':');
  const parseInteger = (input: string): number | undefined => {
    if (!/^[+-]?\d+$/.test(input.trim())) {
      return undefined;
    }
    const parsed = Number(input);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
  };

  if (delimiterIndex > 0) {
    const hours = parseInteger(text.substring(0, delimiterIndex));
    if (hours == null || delimiterIndex >= text.length - 1) {
      return undefined;
    }

    const minutes = parseInteger(text.substring(delimiterIndex + 1));
    return minutes == null ? undefined : (hours * 60) + minutes;
  }

  const hours = parseInteger(text);
  return hours == null ? undefined : hours * 60;
}

export function isD182MaterialName(name?: string | null): boolean {
  return (name ?? '').trim().toUpperCase() === SLURRY_D182_PH_NAME.toUpperCase();
}

export function normalizeDryAdditiveByWeightOfTypeId(name?: string | null, byWeightOfTypeId?: number | null): number {
  if (isD182MaterialName(name)) {
    return byWeightOfTypeId === 1 || byWeightOfTypeId === 2 ? byWeightOfTypeId : 0;
  }

  return byWeightOfTypeId === 2 ? 2 : 1;
}

export function isFluidBasedPercentMaterialId(materialId: number): boolean {
  return materialId === POTASSIUM_PERCENT_MATERIAL_ID || materialId === SODIUM_PERCENT_MATERIAL_ID;
}

export function isFluidBasedWeightMaterialId(materialId: number): boolean {
  return materialId === POTASSIUM_WEIGHT_MATERIAL_ID || materialId === SODIUM_WEIGHT_MATERIAL_ID;
}

export function isFluidBasedMaterialId(materialId: number): boolean {
  return isFluidBasedPercentMaterialId(materialId) || isFluidBasedWeightMaterialId(materialId);
}

export function densityFromAbsoluteVolume(absVolume: number): number {
  return absVolume > 0 ? 1 / absVolume : 0;
}

export function resolveDryMaterialDensity(density: unknown, absoluteVolume: unknown): number {
  const persistedDensity = toFiniteNumber(density);
  return persistedDensity > 0
    ? persistedDensity
    : densityFromAbsoluteVolume(toFiniteNumber(absoluteVolume));
}

export function absoluteVolumeFromSpecificGravity(specificGravity: number): number {
  const density = specificGravity * WATER_DENSITY;
  return density > 0 ? 1 / density : 0;
}

export function getFluidBasedSpecificGravity(materialId: number, percent: number): number {
  const grid = getFluidBasedSpecificGravityTable(materialId);
  if (!grid) {
    return 0;
  }

  const drySpecificGravity = isPotassiumMaterialId(materialId)
    ? POTASSIUM_DRY_SPECIFIC_GRAVITY
    : SODIUM_DRY_SPECIFIC_GRAVITY;
  const splines = buildNaturalSpline(grid);
  const length = splines.length;

  if (percent < 0) {
    return 0;
  }

  let spline = splines[1];
  let dryAmount = 0;
  let x = percent;

  if (x <= splines[0].x) {
    spline = splines[1];
  } else if (x >= splines[length - 1].x) {
    spline = splines[length - 1];
    dryAmount = x - splines[length - 1].x;
    x = splines[length - 1].x;
  } else {
    spline = findSplineSegment(splines, x);
  }

  const dx = x - spline.x;
  let result = spline.a + (spline.b + ((spline.c / 2) + ((spline.d * dx) / 6)) * dx) * dx;
  if (dryAmount > 0) {
    const max = splines[length - 1].x;
    result = ((result * max) / (max + dryAmount)) + ((drySpecificGravity * dryAmount) / (max + dryAmount));
  }

  return result;
}

function getFluidBasedSpecificGravityTable(materialId: number): ReadonlyArray<readonly [number, number]> | null {
  if (materialId === POTASSIUM_PERCENT_MATERIAL_ID || materialId === POTASSIUM_WEIGHT_MATERIAL_ID) {
    return POTASSIUM_SPECIFIC_GRAVITY_TABLE;
  }

  if (materialId === SODIUM_PERCENT_MATERIAL_ID || materialId === SODIUM_WEIGHT_MATERIAL_ID) {
    return SODIUM_SPECIFIC_GRAVITY_TABLE;
  }

  return null;
}

function isPotassiumMaterialId(materialId: number): boolean {
  return materialId === POTASSIUM_PERCENT_MATERIAL_ID || materialId === POTASSIUM_WEIGHT_MATERIAL_ID;
}

function buildNaturalSpline(grid: ReadonlyArray<readonly [number, number]>): NaturalSplinePoint[] {
  const splines = grid.map(point => ({ x: point[0], a: point[1], b: 0, c: 0, d: 0 }));
  const length = splines.length;
  splines[0].c = 0;
  splines[length - 1].c = 0;

  const alpha = new Array<number>(length - 1).fill(0);
  const beta = new Array<number>(length - 1).fill(0);
  for (let i = 1; i < length - 1; i++) {
    const hi = grid[i][0] - grid[i - 1][0];
    const hi1 = grid[i + 1][0] - grid[i][0];
    const z = (hi * alpha[i - 1]) + (2 * (hi + hi1));
    alpha[i] = -hi1 / z;
    beta[i] = (6 * (((grid[i + 1][1] - grid[i][1]) / hi1) - ((grid[i][1] - grid[i - 1][1]) / hi)) - (hi * beta[i - 1])) / z;
  }

  for (let i = length - 2; i > 0; i--) {
    splines[i].c = (alpha[i] * splines[i + 1].c) + beta[i];
  }

  for (let i = length - 1; i > 0; i--) {
    const hi = grid[i][0] - grid[i - 1][0];
    splines[i].d = (splines[i].c - splines[i - 1].c) / hi;
    splines[i].b = ((hi * ((2 * splines[i].c) + splines[i - 1].c)) / 6) + ((grid[i][1] - grid[i - 1][1]) / hi);
  }

  return splines;
}

function findSplineSegment(splines: NaturalSplinePoint[], x: number): NaturalSplinePoint {
  let left = 0;
  let right = splines.length - 1;
  while (left + 1 < right) {
    const mid = left + Math.floor((right - left) / 2);
    if (x <= splines[mid].x) {
      right = mid;
    } else {
      left = mid;
    }
  }

  return splines[right];
}
